import {useEffect, useRef, useState} from "react";
import clsx from "clsx";

export interface CustomSelectOption {
    value: string;
    label: string;
}

interface CustomSelectWhiteProps {
    value: string;
    onChange: (value: string) => void;
    options: CustomSelectOption[];
    placeholder?: string;
    hasError?: boolean;
    disabled?: boolean;
    id?: string;
}

const CustomSelectWhite = ({
    value,
    onChange,
    options,
    placeholder = "Select an option",
    hasError = false,
    disabled = false,
    id,
}: CustomSelectWhiteProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find((option) => option.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    return (
        <div
            ref={containerRef}
            id={id}
            className={clsx("custom-select-white", hasError && "is-invalid", disabled && "is-disabled")}
        >
            <button
                type="button"
                className="custom-select-white__toggle"
                onClick={() => !disabled && setIsOpen((prev) => !prev)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className={clsx(!selectedOption && "custom-select-white__placeholder")}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <span className="custom-select-white__chevron" aria-hidden="true" />
            </button>

            {isOpen && (
                <ul className="custom-select-white__menu" role="listbox">
                    {options.map((option) => (
                        <li key={option.value}>
                            <button
                                type="button"
                                role="option"
                                aria-selected={value === option.value}
                                className={clsx(
                                    "custom-select-white__option",
                                    value === option.value && "is-selected"
                                )}
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CustomSelectWhite;
